// src/screens/PlaceOrderScreen.jsx
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { CheckCircle, MapPin, CreditCard, ShoppingBag, Truck, FileText, ChevronLeft, Loader2, AlertCircle, Package, Clock, ShieldCheck, Sparkles } from 'lucide-react';
import { createOrder } from '../redux/features/Order/orderThunk';
import { reset } from '../redux/features/Order/orderSlice';
import { calculatePrices } from '../utils/utils';
import CheckoutSteps from '../components/CheckoutSteps';

const COLORS = {
  primary: '#8A9A7B',
  primaryLight: '#9EAD8C',
  primaryDark: '#6A7A5B',
  primaryBg: '#F0F4EB',
  border: '#DCE4D3',
  danger: '#D4837A',
  success: '#7A9B8A',
};

const formatPrice = (price) => {
  return Math.round(price).toLocaleString('fa-IR');
};

const OrderItemCard = ({ item }) => {
  return (
    <div className="flex items-start gap-4 p-3 rounded-xl bg-white border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex-shrink-0">
        <img
          src={item.image || 'https://via.placeholder.com/80'}
          alt={item.name}
          className="w-20 h-20 rounded-xl object-cover bg-gray-50"
          onError={(e) => { e.target.src = 'https://via.placeholder.com/80?text=No+Image'; }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <Link to={`/product/${item.product}`} className="block">
          <h4 className="font-bold text-sm text-gray-800 hover:text-[#8A9A7B] transition-colors line-clamp-2">{item.name}</h4>
        </Link>
        {item.selectedColor && (
          <div className="flex items-center gap-1.5 mt-1">
            <span className="text-xs text-gray-400">رنگ:</span>
            <span className="w-3.5 h-3.5 rounded-full border border-gray-200" style={{ backgroundColor: item.selectedColor }} />
            <span className="text-xs text-gray-500">{item.selectedColorName || 'نامشخص'}</span>
          </div>
        )}
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-400">تعداد: {item.qty}</span>
          <span className="text-sm font-bold text-[#8A9A7B]">
            {formatPrice(item.qty * item.price)} تومان
          </span>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ title, icon: Icon, children, className = '' }) => {
  return (
    <div className={`rounded-2xl border border-gray-200 bg-white overflow-hidden ${className}`}>
      <div className="flex items-center gap-2.5 px-4 py-3.5 border-b border-gray-100 bg-gray-50/80">
        <Icon className="w-5 h-5 text-[#8A9A7B]" />
        <h3 className="font-bold text-sm text-[#3D4A33]">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
};

function PlaceOrderScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cart = useSelector((state) => state.cart);
  const { userInfo } = useSelector((state) => state.auth) || {};
  const { loading, error, success, order } = useSelector((state) => state.orderCreate) || {};

  const { itemsPrice, shippingPrice, taxPrice, totalPrice } = calculatePrices(cart);

  const placeOrderHandler = () => {
    dispatch(createOrder({
      orderItems: cart.cartItems,
      shippingAddress: cart.shippingAddress,
      paymentMethod: cart.paymentMethod,
      itemsPrice: parseFloat(itemsPrice),
      shippingPrice: parseFloat(shippingPrice),
      taxPrice: parseFloat(taxPrice),
      totalPrice: parseFloat(totalPrice),
    }));
  };

  useEffect(() => {
    if (!userInfo?.access) {
      navigate('/phone?redirect=/placeorder');
      return;
    }
    if (success && order) {
      const orderId = order._id || order.id;
      if (orderId) {
        navigate(`/order/${orderId}`);
        dispatch(reset());
      } else {
        console.error('❌ Order created but no ID returned:', order);
      }
    }
  }, [success, order, navigate, userInfo, dispatch]);

  useEffect(() => {
    if (error) console.error('❌ Order creation error:', error);
  }, [error]);

  const isCartEmpty = cart.cartItems.length === 0;
  const hasShipping = cart.shippingAddress?.address;
  const hasPayment = cart.paymentMethod;
  const isReady = !isCartEmpty && hasShipping && hasPayment && !loading;

  return (
    <div className="min-h-screen bg-[#F0F4EB] py-8 px-4" dir="rtl">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6"><CheckoutSteps step1 step2 step3 step4 /></div>
        <div className="mt-6">
          <h1 className="text-2xl md:text-3xl font-bold text-[#3D4A33] flex items-center gap-3">
            <FileText className="w-8 h-8 text-[#8A9A7B]" /> تأیید و ثبت سفارش
          </h1>
          <p className="text-gray-500 text-sm mt-1">اطلاعات سفارش خود را بررسی کنید و سپس ثبت نهایی را انجام دهید.</p>
        </div>

        {isCartEmpty && (
          <div className="mt-4">
            <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-700 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> سبد خرید شما خالی است. لطفاً محصولات خود را اضافه کنید.
            </div>
          </div>
        )}
        {!hasShipping && !isCartEmpty && (
          <div className="mt-4">
            <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-700 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> آدرس ارسال انتخاب نشده است. لطفاً به صفحه آدرس بازگردید.
            </div>
          </div>
        )}
        {!hasPayment && !isCartEmpty && (
          <div className="mt-4">
            <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-700 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> روش پرداخت انتخاب نشده است. لطفاً به صفحه پرداخت بازگردید.
            </div>
          </div>
        )}

        {!isCartEmpty && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <SummaryCard title="آدرس ارسال" icon={MapPin}>
                {hasShipping ? (
                  <div className="space-y-1 text-sm">
                    <p className="font-medium text-gray-800">{cart.shippingAddress.address}</p>
                    <p className="text-gray-500">{cart.shippingAddress.city}، {cart.shippingAddress.province || cart.shippingAddress.country}</p>
                    {cart.shippingAddress.postalCode && <p className="text-gray-400 text-xs">کد پستی: {cart.shippingAddress.postalCode}</p>}
                    {cart.shippingAddress.phone && <p className="text-gray-400 text-xs">تلفن: {cart.shippingAddress.phone}</p>}
                  </div>
                ) : (
                  <p className="text-sm text-red-500">آدرس ارسال ثبت نشده است</p>
                )}
              </SummaryCard>

              <SummaryCard title="روش پرداخت" icon={CreditCard}>
                {hasPayment ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-800">
                      {cart.paymentMethod === 'zibal' ? 'زیبال' : cart.paymentMethod === 'snapppay' ? 'اسنپ پی' : cart.paymentMethod}
                    </span>
                    <span className="text-xs text-[#7A9B8A] bg-[#F0F4EB] px-2.5 py-0.5 rounded-full">امن</span>
                  </div>
                ) : (
                  <p className="text-sm text-red-500">روش پرداخت انتخاب نشده است</p>
                )}
              </SummaryCard>

              <SummaryCard title="محصولات سفارش" icon={ShoppingBag}>
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {cart.cartItems.map((item, index) => (
                    <OrderItemCard key={`${item.product}-${item.selectedColor || index}`} item={item} />
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                  تعداد کل: {cart.cartItems.reduce((sum, item) => sum + item.qty, 0)} عدد
                </div>
              </SummaryCard>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-6">
                <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
                  <div className="px-4 py-3.5 bg-gradient-to-l from-[#8A9A7B] to-[#9EAD8C] text-white">
                    <h3 className="font-bold text-sm flex items-center gap-2">
                      <FileText className="w-5 h-5" /> خلاصه فاکتور
                    </h3>
                  </div>
                  <div className="p-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">جمع محصولات</span>
                      <span className="font-medium text-gray-800">{formatPrice(parseFloat(itemsPrice))} تومان</span>
                    </div>
                    {/* ✅ تغییر: هزینه ارسال → پس کرایه */}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">پس کرایه</span>
                      <span className="font-medium text-gray-800">{formatPrice(parseFloat(shippingPrice))} تومان</span>
                    </div>
                    <div className="border-t border-gray-200 pt-3 mt-1">
                      <div className="flex justify-between items-center">
                        <span className="text-base font-bold text-[#3D4A33]">مبلغ نهایی</span>
                        <span className="text-xl font-black text-[#8A9A7B]">{formatPrice(parseFloat(totalPrice))} تومان</span>
                      </div>
                    </div>

                    <button
                      onClick={placeOrderHandler}
                      disabled={!isReady}
                      className={`w-full py-3.5 rounded-xl font-bold text-white text-base transition-all duration-300 flex items-center justify-center gap-2 mt-2 ${
                        isReady
                          ? 'bg-[#8A9A7B] hover:bg-[#6A7A5B] shadow-lg hover:shadow-xl active:scale-[0.98]'
                          : 'bg-gray-300 cursor-not-allowed'
                      }`}
                    >
                      {loading ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /> در حال ثبت سفارش...</>
                      ) : (
                        <><CheckCircle className="w-5 h-5" /> ثبت سفارش نهایی</>
                      )}
                    </button>

                    {error && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-red-600">{error}</span>
                      </div>
                    )}
                    {loading && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-[#8A9A7B]" />
                        <span className="text-xs text-[#8A9A7B]">در حال پردازش...</span>
                      </div>
                    )}

                    <div className="mt-3 pt-3 border-t border-gray-100 flex flex-col gap-1.5 text-[10px] text-gray-400">
                      <div className="flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5 text-[#8A9A7B]" />
                        <span>اطلاعات شما با بالاترین سطح امنیت محافظت می‌شود</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-[#8A9A7B]" />
                        <span>زمان ثبت سفارش: {new Date().toLocaleTimeString('fa-IR')}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => navigate(-1)}
                  className="mt-3 w-full py-2.5 text-sm font-medium text-gray-500 hover:text-[#8A9A7B] transition-colors flex items-center justify-center gap-1.5"
                >
                  <ChevronLeft className="w-4 h-4" /> بازگشت به مرحله قبل
                </button>
              </div>
            </div>
          </div>
        )}

        {isCartEmpty && (
          <div className="mt-6 bg-white rounded-2xl shadow-xl p-8 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">سبد خرید شما خالی است</p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 px-6 py-2.5 bg-[#8A9A7B] text-white rounded-xl hover:bg-[#6A7A5B] transition-colors"
            >
              رفتن به فروشگاه
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PlaceOrderScreen;